import {
  Autocomplete,
  Box,
  Divider,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import type { FilterOptionsState } from "@mui/material";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import { useMemo, useState, type ReactNode } from "react";

// Sentinel option pinned to the end of the list: picking it fires `onAction`
// (e.g. "New provider...") instead of becoming the value.
const ACTION = Symbol("entity-autocomplete-action");
interface ActionOption {
  readonly [ACTION]: true;
}
type Option<T> = T | ActionOption;
const ACTION_OPTION: ActionOption = { [ACTION]: true };

const isAction = <T,>(option: Option<T>): option is ActionOption =>
  (option as unknown as ActionOption)[ACTION] === true;

interface Props<T> {
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionKey: (option: T) => string | number;
  // Text shown in the field once an option is picked.
  getOptionLabel: (option: T) => string;
  // Text the search matches against; defaults to getOptionLabel.
  getSearchText?: (option: T) => string;
  // Custom option body (e.g. two lines); defaults to the label.
  renderOptionContent?: (option: T) => ReactNode;
  label: string;
  placeholder?: string;
  helperText?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  noOptionsText?: ReactNode;
  // Hide the clear button so the field cannot be emptied by the user.
  disableClearable?: boolean;
  // Pinned trailing entry ("+ New provider...") - both must be given.
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * Searchable single-select over platform entities (providers, connections,
 * ...) with an optional pinned "create new" entry at the bottom of the list.
 *
 * Replaces the plain `TextField select` pickers, which showed the whole list
 * as a scrolling menu without search: once every row is loaded (see
 * `features/integrationsBeta/paginatedList.ts`) a picker needs filtering.
 * Styling and scrolling follow `IntegrationClientSelect`.
 */
export default function EntityAutocomplete<T>({
  options,
  value,
  onChange,
  getOptionKey,
  getOptionLabel,
  getSearchText,
  renderOptionContent,
  label,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  loading = false,
  required = false,
  noOptionsText,
  disableClearable = false,
  actionLabel,
  onAction,
}: Props<T>) {
  const hasAction = Boolean(actionLabel && onAction);
  // Controlled so that picking the action entry leaves the field empty: MUI
  // would otherwise echo the action label into the input as if it were the
  // selected value.
  const [inputValue, setInputValue] = useState("");

  const allOptions = useMemo<Option<T>[]>(
    () => (hasAction ? [...options, ACTION_OPTION] : options),
    [options, hasAction],
  );

  const matchReal = useMemo(
    () =>
      createFilterOptions<Option<T>>({
        stringify: (option) =>
          isAction(option) ? "" : (getSearchText ?? getOptionLabel)(option),
      }),
    [getSearchText, getOptionLabel],
  );

  // Real options are filtered by the typed text; the action entry always
  // stays, so "create new" is one click away even with no matches.
  const filterOptions = (
    candidates: Option<T>[],
    state: FilterOptionsState<Option<T>>,
  ): Option<T>[] => {
    const real = matchReal(
      candidates.filter((option) => !isAction(option)),
      state,
    );
    return hasAction ? [...real, ACTION_OPTION] : real;
  };

  return (
    <Autocomplete<Option<T>, false, boolean, false>
      options={allOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, text, reason) => {
        if (reason === "reset" && hasAction && text === actionLabel) {
          setInputValue("");
          return;
        }
        setInputValue(text);
      }}
      onChange={(_, picked) => {
        if (picked && isAction(picked)) {
          onAction?.();
          return;
        }
        onChange(picked ?? null);
      }}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        isAction(option) ? (actionLabel ?? "") : getOptionLabel(option)
      }
      isOptionEqualToValue={(a, b) =>
        !isAction(a) && !isAction(b) && getOptionKey(a) === getOptionKey(b)
      }
      disableClearable={disableClearable}
      disabled={disabled}
      loading={loading}
      autoHighlight
      noOptionsText={noOptionsText}
      ListboxComponent={AutocompleteScrollListbox}
      componentsProps={autocompleteScrollPaperProps}
      renderOption={(props, option) =>
        isAction(option) ? (
          <Box component="li" {...props} key="entity-autocomplete-action">
            <Box sx={{ width: "100%" }}>
              <Divider sx={{ mb: 0.75 }} />
              <Typography variant="body2" color="primary">
                + {actionLabel}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box component="li" {...props} key={getOptionKey(option)}>
            <Box sx={{ minWidth: 0 }}>
              {renderOptionContent ? (
                renderOptionContent(option)
              ) : (
                <Typography variant="body2">
                  {getOptionLabel(option)}
                </Typography>
              )}
            </Box>
          </Box>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          required={required}
        />
      )}
    />
  );
}
